import { AiFiltersFormType, imageSizeType } from "utils";
import { saveAs } from "file-saver";
import { AspectRatio, AiFilterItemType } from "./types";
import { type AiFilterResponseType } from "../../services";

// Video filter types: IntroVideo = 2, WelcomeVideo = 3
const VIDEO_FILTER_TYPES = [2, 3];

export function mapAiImages(
  images: AiFilterResponseType[],
  filterValue: AiFiltersFormType
): AiFilterItemType[] | [] {
  if (!images || images.length === 0) return [];
  return images?.map((item) => {
    const imgSize = getImageSizes(filterValue.aspectRatio as AspectRatio);
    const isVideoFilter = VIDEO_FILTER_TYPES.includes(item.type);
    
    // First check new type-based preview fields (preview16x9Url, etc.)
    const aspectRatioKey = filterValue.aspectRatio; // e.g., "16x9"
    const newPreviewUrl = item[`preview${aspectRatioKey}Url` as keyof AiFilterResponseType] as string | undefined;
    const newThumbnailUrl = item[`preview${aspectRatioKey}ThumbnailUrl` as keyof AiFilterResponseType] as string | undefined;
    
    // If new preview exists, use it
    if (newPreviewUrl) {
      return {
        id: item.id,
        name: item.name,
        url: newPreviewUrl,
        thumbnail: newThumbnailUrl || newPreviewUrl,
        width: imgSize.width,
        height: imgSize.height,
        isVideo: isVideoFilter,
        filterType: item.type,
      };
    }
    
    // Fallback to old gender-based previews
    const preview = item.previews.find(
      (p) =>
        p.aspectRatio === filterValue.aspectRatio &&
        p.gender === filterValue.gender
    );
    return {
      id: item.id,
      name: item.name,
      url: preview?.url,
      thumbnail: preview?.thumbnailUrl,
      width: imgSize.width,
      height: imgSize.height,
      isVideo: isVideoFilter,
      filterType: item.type,
    };
  });
}

export const AiSelectableWrapperOptions = {
  bgOpacity: 0.95,
  close: false,
  padding: { top: 0, bottom: 0, left: 0, right: 300 },
};

export function getImageSizes(aspectRatio?: AspectRatio): imageSizeType {
  const sizes = { width: 200, height: 200 };

  switch (aspectRatio) {
    case AspectRatio.ratio1x1:
      sizes.width = 1024;
      sizes.height = 1024;
      break;
    case AspectRatio.ratio16x9:
      sizes.width = 1344;
      sizes.height = 768;
      break;
    case AspectRatio.ratio9x16:
      sizes.width = 768;
      sizes.height = 1344;
      break;
    case AspectRatio.ratio4x3:
      sizes.width = 1184;
      sizes.height = 864;
      break;
    case AspectRatio.ratio3x4:
      sizes.width = 864;
      sizes.height = 1184;
      break;
    default:
      break;
  }
  return sizes;
}

export function getAIFiltersPhotoSwipeCustomButtons(
  filteredItems: AiFilterItemType[]
) {
  return [
    {
      name: "download-button",
      className: "",
      order: 9,
      isButton: true,
      html: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="text-white mx-auto scale-[1.3]">  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/><path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/></svg>',
      onClick: (e: any, el: any, pswpInstance: any) => {
        const index = pswpInstance.currIndex;
        const { url, name } = filteredItems[index];

        if (url) {
          saveAs(url, name);
        }
      },
    },
    {
      name: "close-button",
      className: "",
      order: 20,
      isButton: true,
      html: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="text-white mx-auto scale-[2]"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/></svg>',
      onClick: (e: any, el: any, pswpInstance: any) => pswpInstance.close(),
    },
  ];
}

export function convertSelectedFiltersToString(items: string[]): string {
  return items.join("\n");
}
export function convertStringToSelectedFilters(text: string): string[] {
  if (!text || typeof text !== "string") return [];

  return text.split("\n").filter((item) => item !== "");
}

export function filterList(
  originalArray: AiFilterItemType[],
  filterArray: string[],
  exclude = false
): AiFilterItemType[] | [] {
  return originalArray.filter((item) =>
    exclude ? !filterArray.includes(item.name) : filterArray.includes(item.name)
  );
}
